import { useState, useEffect } from 'react';
import { cliTokenReader } from '../services/cli-token-reader';

// Global singleton for auth state
let globalAuthState: {
  isLoading: boolean;
  hasProAccess: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: "lifetime" | "free";
  subscription: {
    status: "active" | "inactive";
    plan: "lifetime" | "free";
  };
  isAdmin: boolean;
} = {
  isLoading: true,
  hasProAccess: false,
  isAuthenticated: false,
  subscriptionPlan: "free",
  subscription: {
    status: "inactive",
    plan: "free",
  },
  isAdmin: false,
};

let isInitialized = false;
const subscribers: Array<() => void> = [];

// Cache configuration
const CACHE_KEY = 'moonui_license_cache';

// Dynamic cache duration based on runtime environment
function getCacheDuration(): number {
  if (typeof window !== 'undefined') {
    const isDev = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' ||
                  window.location.hostname.includes('.local');
    return isDev ? 5 * 60 * 1000 : 2 * 60 * 60 * 1000;
  }
  return 2 * 60 * 60 * 1000; // Default to production cache
}

function getOfflineGracePeriod(): number {
  if (typeof window !== 'undefined') {
    const isDev = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' ||
                  window.location.hostname.includes('.local');
    return isDev ? 10 * 60 * 1000 : 4 * 60 * 60 * 1000;
  }
  return 4 * 60 * 60 * 1000; // Default to production grace period
}

interface LicenseCache {
  valid: boolean;
  hasLifetimeAccess: boolean;
  timestamp: number;
  cacheUntil?: number;
}

function notifySubscribers() {
  subscribers.forEach(callback => callback());
}

function updateGlobalState(newState: Partial<typeof globalAuthState>) {
  globalAuthState = { ...globalAuthState, ...newState };
  notifySubscribers();
}

async function getAuthToken(): Promise<string | null> {
  // Runtime environment detection - check hostname instead of NODE_ENV
  const isDevelopment = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('.local') ||
    window.location.port === '3000' ||
    window.location.port === '3001'
  );
  
  const isProduction = !isDevelopment;
  
  console.log('[MoonUI Pro Auth] Getting auth token...', {
    isDevelopment,
    isProduction,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    port: typeof window !== 'undefined' ? window.location.port : 'N/A'
  });
  
  // DEVELOPMENT: Try CLI token first
  if (isDevelopment) {
    // Try to get CLI token
    const cliToken = await cliTokenReader.getCLIToken();
    if (cliToken) {
      console.log('[MoonUI Pro Auth] Using CLI token');
      return cliToken;
    }
    console.log('[MoonUI Pro Auth] No CLI token available');
    
    // Fallback to localStorage in development
    if (typeof window !== 'undefined') {
      const browserToken = localStorage.getItem('moonui_auth_token');
      if (browserToken) {
        console.log('[MoonUI Pro Auth] Found token in localStorage (dev fallback)');
        return browserToken;
      }
    }
    
    // DO NOT use environment variables in development
    console.log('[MoonUI Pro Auth] No valid token found in development - CLI login required');
    return null;
  }
  
  // PRODUCTION: Check for public environment variables or localStorage
  if (isProduction) {
    // First check for NEXT_PUBLIC environment variable (available in browser)
    const publicToken = process.env.NEXT_PUBLIC_MOONUI_AUTH_TOKEN || 
                       process.env.NEXT_PUBLIC_MOONUI_LICENSE_KEY;
    
    if (publicToken && publicToken.trim() !== '') {
      console.log('[MoonUI Pro Auth] Found public token in environment');
      return publicToken;
    }
    
    // Then check localStorage for user-specific auth
    if (typeof window !== 'undefined') {
      const browserToken = localStorage.getItem('moonui_auth_token');
      if (browserToken) {
        console.log('[MoonUI Pro Auth] Found token in localStorage');
        return browserToken;
      }
    }
    
    // No token found in production
    console.log('[MoonUI Pro Auth] No token found in production');
    return null;
  }
  
  return null;
}

async function validateLicense() {
  const token = await getAuthToken();
  
  console.log('[MoonUI Pro Auth] Validating license:', {
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    tokenPreview: token ? `${token.substring(0, 10)}...` : 'null'
  });
  
  if (!token) {
    console.log('[MoonUI Pro Auth] No token found, clearing cache');
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CACHE_KEY);
    }
    updateGlobalState({
      hasProAccess: false,
      isAuthenticated: false,
      isLoading: false
    });
    return;
  }
  
  // Skip CLI-specific logic - use standard validation for all tokens
  
  try {
    console.log('[MoonUI Pro Auth] Calling validate API...');
    const response = await fetch('https://moonui.dev/api/auth/validate', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      
      console.log('[MoonUI Pro Auth] API Response:', {
        valid: data.valid,
        hasLifetimeAccess: data.user?.hasLifetimeAccess,
        features: data.user?.features,
        plan: data.user?.plan
      });
      
      // Cache the response
      const cacheData: LicenseCache = {
        valid: data.valid,
        hasLifetimeAccess: data.user?.hasLifetimeAccess || false,
        timestamp: Date.now(),
        cacheUntil: data.cacheUntil ? new Date(data.cacheUntil).getTime() : undefined
      };
      
      if (typeof window !== 'undefined') {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      }
      
      const hasProAccess = data.valid && (data.user?.hasLifetimeAccess || data.user?.features?.includes('pro_components'));
      
      console.log('[MoonUI Pro Auth] Access result:', {
        hasProAccess,
        isAuthenticated: data.valid
      });
      
      updateGlobalState({
        hasProAccess,
        isAuthenticated: data.valid,
        subscriptionPlan: hasProAccess ? "lifetime" : "free",
        subscription: {
          status: hasProAccess ? "active" : "inactive",
          plan: hasProAccess ? "lifetime" : "free"
        },
        isLoading: false
      });
    } else {
      console.log('[MoonUI Pro Auth] Invalid response:', response.status);
      // Invalid response, clear cache
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CACHE_KEY);
      }
      updateGlobalState({
        hasProAccess: false,
        isAuthenticated: false,
        isLoading: false
      });
    }
  } catch (error) {
    // Network error behavior based on environment
    const isDev = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('.local')
    );
    
    if (isDev && typeof window !== 'undefined') {
      // In development, allow cached access during grace period
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cacheData: LicenseCache = JSON.parse(cached);
        const now = Date.now();
        
        // Allow access during grace period even if network is down
        if (now - cacheData.timestamp < getOfflineGracePeriod()) {
          updateGlobalState({
            hasProAccess: cacheData.valid && cacheData.hasLifetimeAccess,
            isAuthenticated: cacheData.valid,
            subscriptionPlan: (cacheData.valid && cacheData.hasLifetimeAccess) ? "lifetime" : "free",
            subscription: {
              status: (cacheData.valid && cacheData.hasLifetimeAccess) ? "active" : "inactive",
              plan: (cacheData.valid && cacheData.hasLifetimeAccess) ? "lifetime" : "free"
            },
            isLoading: false
          });
          return;
        }
      }
    }
    
    // In production or no valid cache, deny access for security
    updateGlobalState({
      hasProAccess: false,
      isAuthenticated: false,
      isLoading: false
    });
  }
}

async function initializeAuth() {
  if (isInitialized) return;
  isInitialized = true;
  
  // Check cached license first
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem(CACHE_KEY);
    
    if (cached) {
      const cacheData: LicenseCache = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - cacheData.timestamp < getCacheDuration()) {
        updateGlobalState({
          hasProAccess: cacheData.valid && cacheData.hasLifetimeAccess,
          isAuthenticated: cacheData.valid,
          subscriptionPlan: (cacheData.valid && cacheData.hasLifetimeAccess) ? "lifetime" : "free",
          subscription: {
            status: (cacheData.valid && cacheData.hasLifetimeAccess) ? "active" : "inactive",
            plan: (cacheData.valid && cacheData.hasLifetimeAccess) ? "lifetime" : "free"
          },
          isLoading: false
        });
        return;
      }
    }
  }
  
  // No valid cache, validate license
  await validateLicense();
  
  // Periodic check disabled - rely on cache TTL instead
  // Cache already handles expiration (1 hour in production, 1 minute in dev)
  // No need for aggressive periodic checks
}

// Force refresh function - clears cache and revalidates
export async function forceRefresh() {
  if (typeof window !== 'undefined') {
    // Clear cache
    localStorage.removeItem(CACHE_KEY);
    console.log('[MoonUI Pro] Cache cleared, forcing refresh...');
  }
  
  // Reset initialization flag
  isInitialized = false;
  
  // Reset global state to loading
  updateGlobalState({
    isLoading: true,
    hasProAccess: false,
    isAuthenticated: false
  });
  
  // Re-initialize auth
  await initializeAuth();
}

// Clear cache function - for CLI login/logout scenarios
export function clearCache() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem('moonui_auth_token');
    console.log('[MoonUI Pro] Auth cache cleared');
  }
  // Also clear CLI token reader cache
  cliTokenReader.clearCache();
}

// Standalone hook that doesn't require SubscriptionProvider
export function useSubscription() {
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    // Initialize auth on first use
    initializeAuth();
    
    // Subscribe to state changes
    const callback = () => forceUpdate({});
    subscribers.push(callback);
    
    // Window focus event - revalidate when user comes back
    const handleFocus = async () => {
      // Check if cache is expired on focus
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const cacheData: LicenseCache = JSON.parse(cached);
          const now = Date.now();
          
          // If cache is expired, revalidate
          if (now - cacheData.timestamp > getCacheDuration()) {
            console.log('[MoonUI Pro] Cache expired on focus, revalidating...');
            await validateLicense();
          }
        } else {
          // No cache, revalidate
          console.log('[MoonUI Pro] No cache on focus, validating...');
          await validateLicense();
        }
      }
    };
    
    // Storage event - detect CLI login/logout from other tabs
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'moonui_auth_token' || e.key === CACHE_KEY) {
        console.log('[MoonUI Pro] Auth storage changed, revalidating...');
        
        // If token was removed
        if (!e.newValue) {
          updateGlobalState({
            hasProAccess: false,
            isAuthenticated: false,
            isLoading: false
          });
        } else {
          // Token added or changed, revalidate
          await forceRefresh();
        }
      }
    };
    
    // Add event listeners
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      const index = subscribers.indexOf(callback);
      if (index > -1) {
        subscribers.splice(index, 1);
      }
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  return {
    ...globalAuthState,
    checkAccess: validateLicense,
    forceRefresh,
    clearCache
  };
}